import { Box, Skeleton } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import { MobileCloseCash } from '../../component/cashregister/MobileCashRegisters';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import useIsMobileShell from '../../hooks/useIsMobileShell';
import { useCloseCash } from './components/useCashActions';
import { parseBRDateToISO, summarizeCashSession } from './shared';

// `/cashregisters/close/:id` deixou de ser uma página de página cheia — a
// ação "Fechar caixa" agora é um drawer real de conciliação por forma de
// pagamento (`CloseCashDrawer`, §5.6), acionado a partir do menu dropdown da
// Lista de caixas e do header do Painel do caixa (ver FRONTEND-TASKS.md,
// item "Fechar caixa"). Acessar esta rota diretamente só redireciona para o
// Painel do caixa, mesmo precedente de `pages/categories/Form.js`.
//
// Shell mobile: a rota volta a ser uma página cheia (`caixas/fechar.html`),
// com a mesma regra/endpoint do drawer (`useCloseCash`).
function CloseBody({ id, cash, resume, summary, onError }) {
    const navigate = useNavigate();
    const sessionStart = useMemo(
        () => ({ label: resume?.details?.open_date || '—', dateIso: parseBRDateToISO(resume?.details?.open_date) }),
        [resume],
    );
    const expected = useMemo(
        () => ({ dinheiro: summary.valorEsperado, pix: summary.pix, cartao: summary.machineGross }),
        [summary],
    );
    const close = useCloseCash({
        open: true,
        cashRegister: { id, name: cash.name },
        sessionStart,
        expected,
        onSuccess: ({ totalCounted, diff }) => navigate(`/cashregisters/view/${id}`, {
            replace: true,
            state: { toast: `Caixa "${cash.name}" fechado com sucesso.`, closed: { totalCounted, diff } },
        }),
        onError,
    });
    return <MobileCloseCash close={close} summary={summary} sessionLabel={sessionStart.label} onCancel={() => navigate(-1)} />;
}

function MobileClosePage({ id }) {
    const [state, setState] = useState({ loading: true, cash: null, resume: null, history: [] });
    const [snack, setSnack] = useState({ open: false, message: '' });
    useAppBarTitle('Fechar caixa');

    useEffect(() => {
        let active = true;
        Promise.all([
            httpClient.get(`/cashregister/${id}`),
            httpClient.get(`/cashregister/${id}/resume`),
            httpClient.get(`/cashregister/${id}/history`).catch(() => ({ data: [] })),
        ]).then(([dataRes, resumeRes, historyRes]) => {
            if (!active) return;
            setState({
                loading: false,
                cash: dataRes.data,
                resume: resumeRes.data,
                history: Array.isArray(historyRes.data) ? historyRes.data : [],
            });
        }).catch((error) => {
            console.error('Erro ao carregar dados do fechamento:', error);
            if (!active) return;
            setState((prev) => ({ ...prev, loading: false }));
            setSnack({ open: true, message: 'Não foi possível carregar os dados deste caixa.' });
        });
        return () => { active = false; };
    }, [id]);

    const summary = useMemo(() => summarizeCashSession(state.resume, state.history), [state.resume, state.history]);

    return (
        <PageScaffold
            links={[]}
            openSnackbar={snack.open}
            setOpenSnackbar={(open) => setSnack((prev) => ({ ...prev, open }))}
            message={snack.message}
            severity="error"
        >
            {state.loading || !state.cash ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Skeleton variant="rounded" height={150} sx={{ borderRadius: '16px' }} />
                    <Skeleton variant="rounded" height={80} sx={{ borderRadius: '14px' }} />
                </Box>
            ) : (
                <CloseBody
                    id={id}
                    cash={state.cash}
                    resume={state.resume}
                    summary={summary}
                    onError={(data) => setSnack({
                        open: true,
                        message: typeof data === 'string' ? data : (data?.message || data?.error || 'Não foi possível concluir a operação.'),
                    })}
                />
            )}
        </PageScaffold>
    );
}

export default function CashRegisterClose() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isMobileShell = useIsMobileShell();

    useEffect(() => {
        if (!isMobileShell) navigate(`/cashregisters/view/${id}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isMobileShell]);

    return isMobileShell ? <MobileClosePage id={id} /> : null;
}
